import { getTopics, ITopicList } from "@/api/topics";
import { AdminPageHeader, SearchField } from "@/components/admin";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate } from "@/utils";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddTopicButton } from "./AddTopicButton";
import { DeleteTopicButton } from "./DeleteTopicButton";
import { EditTopicButton } from "./EditTopicButton";
import { TopicNameLink } from "./TopicNameLink";
import { TopicsExportButton } from "./TopicsExportButton";
import { TopicsImportButton } from "./TopicsImportButton";

const columns: GridColDef<ITopicList>[] = [
    {
        field: "id",
        headerName: "ID",
        display: "flex",
    },
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        minWidth: 200,
        maxWidth: 400,
        flex: 1,
        renderCell: ({ row }) => <TopicNameLink topic={row} />,
    },
    {
        field: "clause_count",
        headerName: "# of Clauses",
        display: "flex",
        type: "number",
        filterable: false,
        sortable: false,
    },
    {
        field: "created_at",
        headerName: "Created At",
        type: "date",
        display: "flex",
        valueFormatter: (value) => formatDate(value),
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        getActions: ({ row }) => [
            <EditTopicButton key="edit" topic={row} />,
            <DeleteTopicButton key="delete" topicId={row.id} />,
        ],
    },
];

export const TopicsTable = () => {
    const [rows, setRows] = useState<ITopicList[]>([]);

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: "name", sort: "asc" },
    ]);

    useEffect(() => {
        setPaginationModel((prev) =>
            prev.page === 0 ? prev : { ...prev, page: 0 }
        );
    }, [debouncedSearch, sortModel]);

    const sortFilter =
        sortModel.length > 0
            ? { sort_by: sortModel[0].field, order: sortModel[0].sort ?? "asc" }
            : {};

    const autoSizeOptions = {
        columns: ["id", "name", "clause_count", "created_at"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const { data, isFetching } = useQuery({
        queryKey: [
            "admin-topics",
            paginationModel.page,
            paginationModel.pageSize,
            debouncedSearch,
            sortModel,
        ],
        queryFn: () =>
            getTopics(
                paginationModel.page + 1,
                paginationModel.pageSize,
                sortFilter,
                debouncedSearch || undefined
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <>
            <AdminPageHeader
                title="Topics"
                subtitle="Group clauses into topics for checklist organization"
            >
                <TopicsImportButton />
                <TopicsExportButton />
                <AddTopicButton />
            </AdminPageHeader>
            <div className="mt-6">
                <div className="mb-4">
                    <SearchField
                        value={search}
                        onChange={setSearch}
                        onClear={handleSearchClear}
                        placeholder="Search topics..."
                    />
                </div>
                <Table
                    loading={isFetching}
                    columns={columns}
                    rows={rows}
                    disableColumnFilter
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    autosizeOptions={autoSizeOptions}
                    autoSize
                />
            </div>
        </>
    );
};
